import { useCallback, useEffect, useRef, useState } from "react";

interface UseInfiniteScrollOptions {
  initialItems?: number;
  itemsPerLoad?: number;
  loadDelay?: number;
}

export function useInfiniteScroll<T>(
  items: T[],
  options: UseInfiniteScrollOptions = {},
) {
  const { initialItems = 6, itemsPerLoad = 6, loadDelay = 300 } = options;

  const [visibleCount, setVisibleCount] = useState(initialItems);
  const [isLoading, setIsLoading] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const prevItemsRef = useRef(items);

  // Reset visible count when items change (e.g., filters applied)
  if (prevItemsRef.current !== items) {
    prevItemsRef.current = items;
    if (visibleCount !== initialItems) {
      setVisibleCount(initialItems);
    }
  }

  const displayedItems = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;

  const loadMore = useCallback(() => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    setTimeout(() => {
      setVisibleCount((prev) => Math.min(prev + itemsPerLoad, items.length));
      setIsLoading(false);
    }, loadDelay);
  }, [isLoading, hasMore, items.length, itemsPerLoad, loadDelay]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, isLoading, loadMore]);

  return {
    displayedItems,
    hasMore,
    isLoading,
    loadMoreRef,
    loadMore,
  };
}
